import React from 'react';
import { User } from 'lucide-react';

type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  name?: string;
  src?: string;
  size?: AvatarSize;
  className?: string;
}

const SIZE_STYLES: Record<AvatarSize, { container: string; text: string; icon: number }> = {
  sm: { container: 'w-7 h-7', text: 'text-2xs', icon: 12 },
  md: { container: 'w-9 h-9', text: 'text-xs', icon: 14 },
  lg: { container: 'w-12 h-12', text: 'text-sm', icon: 16 },
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      name,
      src,
      size = 'md',
      className = '',
    },
    ref,
  ) => {
    const { container, text, icon } = SIZE_STYLES[size];
    const baseClasses = [
      'inline-flex items-center justify-center rounded-full font-medium shrink-0 overflow-hidden',
      container,
      className,
    ].filter(Boolean).join(' ');

    return (
      <div ref={ref} className={baseClasses} aria-label={name ?? 'User avatar'}>
        {src ? (
          <img
            src={src}
            alt={name ? `${name} avatar` : 'Avatar'}
            className="w-full h-full object-cover rounded-full"
          />
        ) : name ? (
          <span
            className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-brand to-[#7c3aed] text-content-inverse ${text}`}
          >
            {getInitials(name)}
          </span>
        ) : (
          <span className="w-full h-full flex items-center justify-center bg-surface-secondary text-content-tertiary">
            <User size={icon} />
          </span>
        )}
      </div>
    );
  },
);

Avatar.displayName = 'Avatar';

export default Avatar;
